import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { modalStyle, headerStyle, bottomCloseButtonStyle } from "./styles";
import { tokens } from "../../Common/designTokens";
import StrategyParamFields from "./StrategyParamFields";
import { deriveParamMeta, validateParamValue } from "./strategyParamUtils";

/**
 * >957 StrategyParametersGrp, one row's worth of it.
 *
 * A single shared dialog ListRfqTab mounts once for the whole grid, not one per row - see
 * ListRfqBondRow's "strategyParameters" column comment for why this field is a modal rather
 * than an inline cell (the definition set is dynamic per strategy type, fetched from the
 * server, and does not fit a fixed-width column). Styled with the same modalStyle/headerStyle
 * BondDetailsModal uses, so it reads as part of the same screen rather than a one-off popup.
 *
 * Purely a thin wrapper around StrategyParamFields - the exact component the single-bond
 * ticket already uses (ActualOrderTabOrderProtocolSection) - reused as-is, not reimplemented.
 * Fetched definitions and which row is open still live in ListRfqTab (the same way
 * BondDetailsModal's own state does), but the row's edited values do not: they are staged in
 * local draft state here and only handed back via onSave, on the trader's explicit OK. Cancel
 * (the Cancel button, the header's close icon, and the backdrop/Escape) discards the draft and
 * leaves the row's actual strategyParameters exactly as they were before this open.
 *
 * `initialValues` seeds the draft exactly once per open - see the effect below - never mid-
 * session, so a value the trader is actively typing can never be clobbered by a stray re-render
 * of the parent.
 */
const StrategyParamsModal = ({
  open,
  onCancel,
  onSave,
  identifier,
  loading,
  error,
  params = [],
  initialValues = {},
  readOnly = false,
}) => {
  const [draftValues, setDraftValues] = useState(initialValues);
  const [fieldErrors, setFieldErrors] = useState({});

  // Seeds the draft from initialValues exactly once per open, at the moment the dialog has
  // something to show (loading just finished) - not on every render, and not again while the
  // same open stays open, so mid-edit keystrokes are never overwritten by a parent re-render
  // that happens to pass a new (but equal) initialValues object. Deliberately NOT keyed on
  // initialValues itself (disabled below): re-running this every time the parent passes a new
  // object reference would reset an in-progress edit - it is read once, via closure, at the
  // open/loading transition.
  const wasOpenLoading = useRef({ open: false, loading: false });
  useEffect(() => {
    const prev = wasOpenLoading.current;
    const justBecameReady = open && !loading && (!prev.open || prev.loading);
    if (justBecameReady) {
      setDraftValues(initialValues);
      setFieldErrors({});
    }
    wasOpenLoading.current = { open, loading };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading]);

  const handleFieldChange = (paramName, value) => {
    setDraftValues((prev) => ({ ...prev, [paramName]: value }));
    setFieldErrors((prev) => ({ ...prev, [paramName]: false }));
  };

  const handleSave = () => {
    const errors = {};
    params.forEach((def) => {
      const value = draftValues[def.paramName];
      if (value !== null && value !== undefined && value !== '' &&
          !validateParamValue(deriveParamMeta(def), value)) {
        errors[def.paramName] = true;
      }
    });
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    onSave(draftValues);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      PaperProps={{
        sx: {
          ...modalStyle,
          width: "700px",
          bgcolor: tokens.colors.bodyBackgroundColor,
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle sx={headerStyle}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          Strategy Parameters{identifier ? ` - ${identifier}` : ""}
        </Typography>
        <IconButton aria-label="close" onClick={onCancel} sx={{ p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading strategy parameters…</Typography>
          </Box>
        )}
        {!loading && error && (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && params.length === 0 && (
          <Typography variant="body2" sx={{ py: 2, color: tokens.colors.fontColour85 }}>
            This strategy has no additional parameters.
          </Typography>
        )}
        {!loading && !error && params.length > 0 && (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <StrategyParamFields
              params={params}
              values={draftValues}
              errors={fieldErrors}
              onChange={handleFieldChange}
              isFieldEditable={() => !readOnly}
              editRfqMode={false}
            />
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ ...bottomCloseButtonStyle, mt: 0 }}>
        <Button variant="outlined" onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            disabled={loading || !!error}
            onClick={handleSave}
            sx={{ textTransform: "none" }}
          >
            OK
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StrategyParamsModal;
